import React, { useState, useCallback, useRef, useEffect } from 'react';
import './App.scss';
import CanvasBackground from './components/CanvasBackground/CanvasBackground';
import ConversationDisplay from './components/ConversationDisplay/ConversationDisplay';
import KeywordInput from './components/KeywordInput/KeywordInput';
import LoadingIndicator from './components/LoadingIndicator/LoadingIndicator';
import MoreButton from './components/MoreButton/MoreButton';
import PlayerControls from './components/PlayerControls/PlayerControls';
import PauseOverlay from './components/PauseOverlay/PauseOverlay';
import TopicDialog from './components/TopicDialog/TopicDialog';
import PromoSlot from './components/PromoSlot/PromoSlot';
import { startConversation as apiStart, continueConversation as apiContinue, fetchTTS } from './services/api';
import { fetchPromo, PLACEMENTS } from './services/promo';
import { unlockPlayer } from './services/audioPlayer';

import CHARACTERS from './data/characters';

const OPENING_DURATION = 3000; // オープニング表示時間(ms)
const FADE_OUT_DURATION = 800; // フェードアウト時間(ms)

const CHARACTER_MAP = {};
CHARACTERS.forEach((c) => {
  CHARACTER_MAP[c.id] = { displayName: c.displayName, color: c.placeholderColor };
});

function App() {
  // マスターの歩行シーケンスの起点（会話開始ボタンで開始。null の間は非表示）
  const [sequenceStart, setSequenceStart] = useState(null);

  const [showOpening, setShowOpening] = useState(true);
  const [openingFadingOut, setOpeningFadingOut] = useState(false);

  const [conversations, setConversations] = useState([]);
  const [currentBlock, setCurrentBlock] = useState([]);
  const [isConversationStarted, setIsConversationStarted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [keyword, setKeyword] = useState('');
  const [currentLineIndex, setCurrentLineIndex] = useState(0);
  const [blockFinished, setBlockFinished] = useState(false);

  // 段階的TTS用
  const audioMapRef = useRef({});
  const [audioReadyIndex, setAudioReadyIndex] = useState(-1);
  const [isWaitingForAudio, setIsWaitingForAudio] = useState(false);
  const ttsAbortRef = useRef(false);

  // 広告。会話ブロックごとに、それまでの会話を読ませて AI に選び直させる
  const [tablePromo, setTablePromo] = useState(null);
  const [bannerPromo, setBannerPromo] = useState(null);
  // 取得の多重実行を防ぐ（バナーはブロック終了時に初めて取りに行く）
  const bannerRequestedRef = useRef(false);
  // AI に渡す会話。setState の反映を待たずに使いたいので ref でも保持する
  const conversationRef = useRef([]);

  // 広告を選び直す。AI 側で時間がかかるので会話の再生は待たせない
  const refreshPromo = useCallback((placementSlug, setter, keywordForAd) => {
    fetchPromo(placementSlug, keywordForAd, conversationRef.current)
      .then((promo) => {
        // 取得中に話題が変わっていたら捨てる
        if (conversationRef.current.length === 0) return;
        if (promo) setter(promo);
      })
      .catch(() => undefined);
  }, []);

  // 再生コントロール
  //   isMuted  … 音だけ消す。会話は進む
  //   isPaused … 会話・音声・アニメーションをまとめて止める
  const [isMuted, setIsMuted] = useState(false);
  const [isPaused, setIsPaused] = useState(false);

  // 会話中に話題を入れ直すポップアップ。開いている間は一時停止状態にする
  const [isTopicDialogOpen, setIsTopicDialogOpen] = useState(false);

  // BGM
  const bgmRef = useRef(null);

  // blob URL クリーンアップ
  const cleanupAudioUrls = useCallback(() => {
    Object.values(audioMapRef.current).forEach((url) => {
      if (url) URL.revokeObjectURL(url);
    });
    audioMapRef.current = {};
  }, []);

  useEffect(() => {
    return () => cleanupAudioUrls();
  }, [cleanupAudioUrls]);

  // オープニング制御
  useEffect(() => {
    const fadeTimer = setTimeout(() => setOpeningFadingOut(true), OPENING_DURATION);
    const hideTimer = setTimeout(() => setShowOpening(false), OPENING_DURATION + FADE_OUT_DURATION);
    return () => { clearTimeout(fadeTimer); clearTimeout(hideTimer); };
  }, []);

  // 1行ずつ順次TTS生成（失敗時リトライ付き）
  const progressiveTTS = useCallback(async (lines) => {
    cleanupAudioUrls();
    setAudioReadyIndex(-1);
    ttsAbortRef.current = false;

    const MAX_TTS_RETRIES = 2;

    for (let i = 0; i < lines.length; i++) {
      if (ttsAbortRef.current) break;

      let url = null;
      for (let attempt = 0; attempt <= MAX_TTS_RETRIES; attempt++) {
        if (ttsAbortRef.current) break;
        try {
          url = await fetchTTS(lines[i].speaker, lines[i].text);
          break; // 成功
        } catch (err) {
          console.warn(`TTS failed for line ${i} (attempt ${attempt + 1}):`, err);
          if (attempt < MAX_TTS_RETRIES) {
            await new Promise((r) => setTimeout(r, 500)); // リトライ前に少し待つ
          }
        }
      }

      if (ttsAbortRef.current) {
        if (url) URL.revokeObjectURL(url);
        break;
      }
      audioMapRef.current[i] = url; // 成功時はURL、全リトライ失敗時はnull
      if (!ttsAbortRef.current) {
        setAudioReadyIndex(i);
      }
    }
  }, [cleanupAudioUrls]);

  // ゲート機構: その行の音声生成が完了（成功/失敗どちらも）したら待機解除
  // ※ TTS 失敗時は audioMap[i]=null だが audioReadyIndex は進むので、
  //   ここで null を許容しないと失敗行で永久待機になり MoreButton が出なくなる
  useEffect(() => {
    if (isWaitingForAudio && audioReadyIndex >= currentLineIndex) {
      setIsWaitingForAudio(false);
    }
  }, [audioReadyIndex, currentLineIndex, isWaitingForAudio]);

  // 再生すべき行インデックス（待機中は -1）
  const activeLineIndex = isWaitingForAudio ? -1 : currentLineIndex;

  // BGM音量制御: 会話再生中は下げる、それ以外は戻す
  const isSpeaking = isConversationStarted && !isLoading && !blockFinished && !isWaitingForAudio;
  useEffect(() => {
    const bgm = bgmRef.current;
    if (!bgm) return;
    bgm.volume = isSpeaking ? 0.15 : 0.5;
    bgm.muted = isMuted;
    // 一時停止中は BGM も止める（ミュートは音量だけの話なので鳴らし続ける）
    if (isPaused) {
      if (!bgm.paused) bgm.pause();
    } else if (bgm.paused) {
      bgm.play().catch(() => {});
    }
  }, [isSpeaking, isMuted, isPaused]);

  const startBgm = () => {
    if (bgmRef.current) return;
    const bgm = new Audio('/img/bgm01.mp3');
    bgm.loop = true;
    bgm.volume = 0.5;
    bgm.muted = isMuted;
    bgm.play().catch(() => {});
    bgmRef.current = bgm;
  };

  const handleStart = async (inputKeyword) => {
    setIsPaused(false);
    // モバイルは「ユーザー操作の中で一度 play() した要素」しか後から鳴らせない。
    // ここで解錠しておかないと、セリフの音声だけ無音になる
    unlockPlayer();
    startBgm();
    setSequenceStart(performance.now()); // マスターが画面左から歩き出す
    setKeyword(inputKeyword);

    // 前の話題の広告は一旦消す。新しい広告は会話が生成できてから選ぶ
    // （AI に会話の流れを読ませるため、セリフが揃うまで待つ）
    setTablePromo(null);
    setBannerPromo(null);
    bannerRequestedRef.current = false;
    conversationRef.current = [];

    setIsConversationStarted(true);
    setIsLoading(true);
    setError(null);
    setBlockFinished(false);
    ttsAbortRef.current = true; // 進行中のTTSを中断

    try {
      const result = await apiStart(inputKeyword);
      const lines = result.lines.map((line, i) => ({
        ...line,
        id: Date.now() + i,
      }));

      setConversations(lines);
      setCurrentBlock(lines);
      setCurrentLineIndex(0);
      setIsLoading(false);
      setIsWaitingForAudio(true); // 1行目のTTS完了を待つ

      // 会話が揃ったので、その内容を読ませて広告を選ぶ
      conversationRef.current = lines;
      refreshPromo(PLACEMENTS.table, setTablePromo, inputKeyword);

      // バックグラウンドで段階的TTS開始
      progressiveTTS(lines);
    } catch (e) {
      setError(e.message);
      setIsLoading(false);
    }
  };

  const handleMore = async () => {
    setIsPaused(false);
    unlockPlayer();
    setSequenceStart(performance.now()); // マスターの歩行シーケンスを頭からやり直す
    setIsLoading(true);
    setError(null);
    setBlockFinished(false);
    ttsAbortRef.current = true; // 進行中のTTSを中断

    try {
      const result = await apiContinue(keyword, conversations);
      const lines = result.lines.map((line, i) => ({
        ...line,
        id: Date.now() + i,
      }));

      setConversations((prev) => [...prev, ...lines]);
      setCurrentBlock(lines);
      setCurrentLineIndex(0);
      setIsLoading(false);
      setIsWaitingForAudio(true);

      // 話が展開したぶんも含めて、広告を選び直す
      conversationRef.current = [...conversationRef.current, ...lines];
      bannerRequestedRef.current = false;
      refreshPromo(PLACEMENTS.table, setTablePromo, keyword);

      progressiveTTS(lines);
    } catch (e) {
      setError(e.message);
      setIsLoading(false);
    }
  };

  // 話題を変える: お題入力ダイアログ（初期状態）に戻す
  const handleChangeTopic = () => {
    setIsPaused(false);
    ttsAbortRef.current = true; // 進行中のTTSを中断
    cleanupAudioUrls();
    setIsConversationStarted(false);
    setConversations([]);
    setCurrentBlock([]);
    setCurrentLineIndex(0);
    setBlockFinished(false);
    setIsWaitingForAudio(false);
    setAudioReadyIndex(-1);
    setError(null);
    setKeyword('');
    // 話題が変われば広告も引き直す
    setTablePromo(null);
    setBannerPromo(null);
    bannerRequestedRef.current = false;
    conversationRef.current = [];
  };

  // 右上の「話題を変える」。
  // isPaused を立てると会話・セリフ音声・BGM・アニメーションがまとめて止まる
  // （BGM の停止は isSpeaking の useEffect が担当）。
  const openTopicDialog = () => {
    setIsPaused(true);
    setIsTopicDialogOpen(true);
  };

  // 入れ直さずに閉じた場合。止める前の状態へそのまま戻す
  const closeTopicDialog = () => {
    setIsTopicDialogOpen(false);
    setIsPaused(false);
  };

  // ポップアップから新しいお題が来た。handleStart 側で一時停止も解除される
  const handleTopicSubmit = (newKeyword) => {
    setIsTopicDialogOpen(false);
    handleStart(newKeyword);
  };

  // バナーは「続きを聞く」ボタンと一緒に出るので、実際に出せる状態になってから選ぶ。
  // ブロックが終わった時点＝そのブロックの会話が出揃った時点なので、
  // AI に読ませる材料としても都合がよい。
  useEffect(() => {
    if (!blockFinished || isLoading || isWaitingForAudio) return;
    if (bannerRequestedRef.current) return;
    bannerRequestedRef.current = true;
    refreshPromo(PLACEMENTS.banner, setBannerPromo, keyword);
  }, [blockFinished, isLoading, isWaitingForAudio, keyword, refreshPromo]);

  // setState の updater 内で別の setState を呼ぶと、updater が複数回実行されたときに
  // 待機フラグが遅れて立ち、activeLineIndex が -1 → 同じ行 と往復して
  // 同じセリフを2回再生してしまう。updater には副作用を持たせない。
  const handleLineComplete = useCallback(() => {
    const next = currentLineIndex + 1;
    if (next >= currentBlock.length) {
      setCurrentLineIndex(next);
      setBlockFinished(true);
      return;
    }
    // 次行がまだ生成完了していなければ待機（null=失敗確定はそのまま進む）
    if (!(next in audioMapRef.current)) {
      setIsWaitingForAudio(true);
    }
    setCurrentLineIndex(next);
  }, [currentLineIndex, currentBlock.length]);

  return (
    <div className="App">
      {/* オープニング */}
      {showOpening && (
        <div className={`opening-overlay${openingFadingOut ? ' fade-out' : ''}`}>
          <img src="/img/logo.svg" alt="僕らの話は終わらない" className="opening-logo" />
        </div>
      )}

      {/* 左上ロゴ（オープニング後に表示） */}
      {!showOpening && (
        <img src="/img/logo.svg" alt="僕らの話は終わらない" className="header-logo" />
      )}

      <CanvasBackground
        sequenceStart={sequenceStart}
        // 広告は会話が始まってから出す（お題入力ダイアログ中は出さない）
        tablePromo={isConversationStarted ? tablePromo : null}
        tablePromoPlacement={PLACEMENTS.table}
        promoKeyword={keyword}
        onPromoClick={() => setIsPaused(true)}
        paused={isPaused}
        activeSpeaker={
          !isLoading && !blockFinished && isConversationStarted && activeLineIndex >= 0 && currentBlock[activeLineIndex]
            ? currentBlock[activeLineIndex].speaker
            : null
        }
      >
        {!isConversationStarted ? (
          <div className="app-content">
            <KeywordInput onSubmit={handleStart} />
          </div>
        ) : (
          <>
            {!isLoading && currentBlock.length > 0 && (
              <ConversationDisplay
                lines={currentBlock}
                activeLineIndex={activeLineIndex}
                onLineComplete={handleLineComplete}
                audioMap={audioMapRef.current}
                paused={isPaused}
                muted={isMuted}
              />
            )}

            {isLoading && <LoadingIndicator />}

            {!isLoading && isWaitingForAudio && (
              <LoadingIndicator message="音声を準備しています" />
            )}

            {error && (
              <div className="error-overlay">
                <div className="titlebar">
                  <div className="dots">
                    <i className="d1" />
                    <i className="d2" />
                    <i className="d3" />
                  </div>
                  <div className="ttl">ERROR</div>
                </div>
                <div className="error-body">
                  <p>{error}</p>
                  <button type="button" onClick={() => setError(null)}>閉じる</button>
                </div>
              </div>
            )}

            {/* 一時停止中はボタンごと隠す。再開は画面中央の再生ボタンで行う */}
            {!isPaused && (
              <PlayerControls
                muted={isMuted}
                onToggleMute={() => setIsMuted((v) => !v)}
                onPause={() => setIsPaused(true)}
                onChangeTopic={openTopicDialog}
              />
            )}

            {/* 話題入力中は再生ボタンを出さない。閉じ方をポップアップ側に一本化する */}
            {isPaused && !isTopicDialogOpen && (
              <PauseOverlay onResume={() => setIsPaused(false)} />
            )}

            {isTopicDialogOpen && (
              <TopicDialog onSubmit={handleTopicSubmit} onCancel={closeTopicDialog} />
            )}

            {!isLoading && !isWaitingForAudio && blockFinished && (
              <MoreButton
                onMore={handleMore}
                onChangeTopic={handleChangeTopic}
                banner={
                  <PromoSlot
                    promo={bannerPromo}
                    placementSlug={PLACEMENTS.banner}
                    keyword={keyword}
                    variant="banner"
                    onPromoClick={() => setIsPaused(true)}
                  />
                }
              />
            )}
          </>
        )}
      </CanvasBackground>
    </div>
  );
}

export default App;
